import { useEffect, useState } from "react";
import {
    CircularProgress,
    Box,
    Typography,
    ImageList,
    ImageListItem,
    IconButton,
    useTheme,
    useMediaQuery
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { IImage } from "@/services/api/Image/types";
import imagesApi from "@/services/api/Image";
import ImageGalleryContainer from "@/components/comercial/ImageGallery/ImageGalleryContainer";
import {ImageEntity} from "@/components/general/Images/types";



interface ImageGalleryManagerApiProps {
    entityId?: string;
    entityType?: ImageEntity;
    images?: IImage[];
}

const ImageGalleryManagerApi = ({ entityId, entityType, images: preloadedImages }: ImageGalleryManagerApiProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const [images, setImages] = useState<IImage[]>([]);
    const [loading, setLoading] = useState<boolean>(!preloadedImages);
    const [error, setError] = useState<boolean>(false);

    const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});

    const getGridColumns = () => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 3;
    };

    const fetchEntityImages = async () => {
        try {
            setLoading(true);
            setError(false);
            const response = await imagesApi.getImagesByEntity(entityType!, entityId!);
            setImages(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error(`Erro ao buscar imagens de ${entityType}:`, err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (preloadedImages) {
            setImages(preloadedImages);
            setLoading(false);
            return;
        }

        if (entityId && entityType) {
            fetchEntityImages();
        }
    }, [entityId, entityType, preloadedImages]);

    const handleDeleteImage = async (imageId: string) => {
        const confirmDelete = window.confirm(
            t("imageGallery.manager.confirmDelete", "Tem certeza que deseja deletar permanentemente esta imagem do banco de dados?")
        );

        if (!confirmDelete) return;

        try {
            setDeletingIds((prev) => ({ ...prev, [imageId]: true }));

            await imagesApi.deleteImageById(imageId);

            setImages((prevImages) => prevImages.filter((img) => img.id !== imageId));
        } catch (err) {
            console.error("Erro ao deletar a imagem no banco:", err);
            window.alert(t("imageGallery.manager.errorDelete", "Erro ao excluir a imagem do servidor. Tente novamente."));
        } finally {
            setDeletingIds((prev) => ({ ...prev, [imageId]: false }));
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={35} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" textAlign="center" sx={{ p: 3, fontWeight: 500 }}>
                {t("imageGallery.manager.errorLoading", "Erro ao carregar imagens para gerenciamento.")}
            </Typography>
        );
    }

    if (images.length === 0) {
        return (
            <Typography color="text.secondary" textAlign="center" sx={{ p: 4, fontStyle: "italic" }}>
                {t("imageGallery.manager.noImages", "Nenhuma imagem cadastrada para este registro.")}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                px: isMobile ? 1 : 0
            }}
        >
            <ImageGalleryContainer style={{ width: "100%" }}>
                <ImageList
                    cols={getGridColumns()}
                    gap={isMobile ? 12 : 16}
                    sx={{
                        width: "100%",
                        maxWidth: "1200px",
                        height: "auto",
                        margin: "0 auto",
                        overflowY: "hidden"
                    }}
                >
                    {images.map((item) => {
                        const isDeleting = deletingIds[item.id];

                        return (
                            <ImageListItem
                                key={item.id}
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    borderRadius: 2,
                                    "& .delete-overlay-btn": {
                                        opacity: isMobile ? 1 : 0,
                                        transition: "opacity 0.2s ease-in-out"
                                    },
                                    "&:hover .delete-overlay-btn": {
                                        opacity: 1
                                    }
                                }}
                            >
                                {isDeleting && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0, left: 0, right: 0, bottom: 0,
                                            bgcolor: "rgba(0, 0, 0, 0.6)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 3
                                        }}
                                    >
                                        <CircularProgress size={24} color="error" />
                                    </Box>
                                )}

                                {!isDeleting && (
                                    <IconButton
                                        className="delete-overlay-btn"
                                        onClick={() => handleDeleteImage(item.id)}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            color: theme.palette.error.main,
                                            bgcolor: "rgba(255, 255, 255, 0.9)",
                                            zIndex: 2,
                                            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                                            "&:hover": {
                                                bgcolor: theme.palette.error.main,
                                                color: "#fff"
                                            }
                                        }}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}

                                <img
                                    src={item.thumbnailUrl}
                                    alt="Miniatura para gerenciamento"
                                    loading="lazy"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        aspectRatio: "16 / 9",
                                        objectFit: "cover",
                                        display: "block",
                                        filter: isDeleting ? "grayscale(1) blur(2px)" : "none"
                                    }}
                                />
                            </ImageListItem>
                        );
                    })}
                </ImageList>
            </ImageGalleryContainer>
        </Box>
    );
};

export default ImageGalleryManagerApi;